import { logger } from "@/common/logger";
import { Upload01Icon, Edit02Icon } from "@/icons";
import { AsideSection } from "@/kit/ui/aside";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalTrigger,
  ResponsiveModalDescription,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/kit/ui/responsive-modal";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment, useMutation } from "react-relay";
import DatasetVersionEditFile from "./DatasetVersionEditFile";
import {
  DatasetVersionTable,
  DatasetVersionTableContextProvider,
} from "./DatasetVersionTable";
import EditMarkdownSection from "./EditMarkdownSection";
import { DatasetVersionEditFileFragment$key } from "./__generated__/DatasetVersionEditFileFragment.graphql";
import { DatasetVersionFragment$key } from "./__generated__/DatasetVersionFragment.graphql";
import { DatasetVersionUpdateMutation as DatasetVersionUpdateMutationType } from "./__generated__/DatasetVersionUpdateMutation.graphql";
import ErrorBoundary from "@/common/ErrorBoundary";
import { WorkspaceView } from "./WorkspaceView";

const DatasetVersionFragment = graphql`
  fragment DatasetVersionFragment on DatasetVersion {
    id
    rows
    schema
    version
    description
    publishedAt
    viewerCanUpdateDatasetVersion: viewerCan(action: UPDATE_DATASET_VERSION)
    viewerCanCreateDatasetVersion: viewerCan(action: CREATE_DATASET_VERSION)
    ...DatasetVersionTableFragment
    ...DatasetVersionEditFileFragment
    workspace {
      id
      editor {
        id
      }
      ...WorkspaceViewFragment
    }
    dataset {
      slug
      name
    }
  }
`;

interface Props {
  datasetVersion: DatasetVersionFragment$key;
}

export function DatasetVersion(props: Props) {
  const datasetVersion = useFragment(
    DatasetVersionFragment,
    props.datasetVersion,
  );
  const workspace = datasetVersion.workspace;
  const editor = workspace?.editor;

  return (
    <AsideSection>
      {datasetVersion?.viewerCanCreateDatasetVersion && (
        <div className="flex sm:flex-row flex-col sm:items-center sm:justify-between gap-2">
          <h2 className="text-xl font-semibold text-grey-900 whitespace-nowrap">
            <FormattedMessage defaultMessage="Dataset viewer" />
          </h2>
          <div className="w-full"></div>
          {datasetVersion.schema && (
            <>
              {editor && (
                <Link
                  to={`/workspaces/${workspace.id}/edit`}
                  variant="secondary"
                  size="sm"
                >
                  <Edit02Icon />
                  <FormattedMessage defaultMessage="Edit notebook" />
                </Link>
              )}
              {datasetVersion.viewerCanCreateDatasetVersion &&
                !datasetVersion.publishedAt && (
                  <DatasetFileDropModal
                    datasetVersion={datasetVersion}
                    name={datasetVersion.dataset.name}
                    version={datasetVersion.version}
                  />
                )}
            </>
          )}
        </div>
      )}
      {datasetVersion.schema ? (
        workspace ? (
          <WorkspaceView workspace={workspace} className="w-full h-full" />
        ) : (
          <>
            <DatasetVersionTableContextProvider>
              <ErrorBoundary>
                <DatasetVersionTable datasetVersion={datasetVersion} />
              </ErrorBoundary>
            </DatasetVersionTableContextProvider>
            <h2 className="text-xl font-semibold text-grey-900">
              <FormattedMessage defaultMessage="About dataset version" />
            </h2>
            <DatasetVersionEditMarkdownSection
              id={datasetVersion.id}
              description={datasetVersion.description}
              viewerCanUpdate={datasetVersion.viewerCanUpdateDatasetVersion}
            />
          </>
        )
      ) : (
        <DatasetVersionEditFile datasetVersion={datasetVersion} />
      )}
    </AsideSection>
  );
}

const DatasetVersionUpdateMutation = graphql`
  mutation DatasetVersionUpdateMutation(
    $id: ID!
    $input: UpdateDatasetVersionInput!
  ) {
    updateDatasetVersion(datasetVersionId: $id, input: $input) {
      id
      description
    }
  }
`;

interface DatasetVersionEditMarkdownSectionProps {
  id: ID;
  viewerCanUpdate?: boolean;
  description?: string | null;
}

function DatasetVersionEditMarkdownSection({
  id,
  viewerCanUpdate,
  description,
}: DatasetVersionEditMarkdownSectionProps) {
  const [commitMutation, isMutationInFlight] =
    useMutation<DatasetVersionUpdateMutationType>(DatasetVersionUpdateMutation);

  return (
    <EditMarkdownSection
      canUploadFiles
      id={id}
      canEdit={viewerCanUpdate}
      disabled={isMutationInFlight}
      content={description ?? ""}
      onSubmit={({ content }) =>
        new Promise((resolve, reject) =>
          commitMutation({
            variables: {
              id,
              input: { description: content || null },
            },
            onCompleted: () => resolve(),
            onError(error) {
              logger.error(error);
              reject(error);
            },
          }),
        )
      }
    />
  );
}

interface DatasetFileDropModalProps {
  name: string;
  version: string;
  datasetVersion: DatasetVersionEditFileFragment$key;
}

function DatasetFileDropModal({
  datasetVersion,

  name,
  version,
}: DatasetFileDropModalProps) {
  const [open, setOpen] = useState(false);
  return (
    <ResponsiveModal open={open} onOpenChange={setOpen}>
      <ResponsiveModalTrigger asChild>
        <Button variant="primary">
          <Upload01Icon />
          <FormattedMessage defaultMessage="Upload data" />
        </Button>
      </ResponsiveModalTrigger>

      <ResponsiveModalContent className="px-6 bg-linear-to-b from-[#EFEDFC] from-[-16.39%] to-white to-[65.62%]">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="text-dxs font-semibold text-grey-900">
            <FormattedMessage defaultMessage="Upload data" />
          </ResponsiveModalTitle>
          <ResponsiveModalDescription className="text-sm font-normal text-grey-600 mb-4">
            <FormattedMessage
              defaultMessage="The file you upload will replace current file connected to “{name}” version “{version}”"
              values={{ name, version }}
            />
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>
        <DatasetVersionEditFile
          datasetVersion={datasetVersion}
          onClose={() => setOpen(false)}
        />
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
